var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { LineChartVariant, DEFAULT_CHART_COLORS } from "../types/charts";
/**
 * Analyzes data structure and generates chart configuration
 */
export function analyzeChartData(data) {
    if (!data || data.length === 0) {
        throw new Error("Data array is empty");
    }
    var firstItem = data[0];
    var allKeys = Object.keys(firstItem);
    // Detect potential x-axis key (usually string/date fields)
    var xAxisKey = allKeys.find(function (key) {
        var value = firstItem[key];
        return typeof value === 'string' ||
            (typeof value === 'number' && key.toLowerCase().includes('time')) ||
            key.toLowerCase().includes('date') ||
            key.toLowerCase().includes('month') ||
            key.toLowerCase().includes('day') ||
            key.toLowerCase().includes('period');
    }) || allKeys[0]; // fallback to first key
    // Detect numeric keys for y-axis
    var numericKeys = allKeys.filter(function (key) {
        if (key === xAxisKey)
            return false;
        var value = firstItem[key];
        return typeof value === 'number' || !isNaN(Number(value));
    });
    if (numericKeys.length === 0) {
        throw new Error("No numeric data found for charting");
    }
    return {
        dataKeys: allKeys,
        xAxisKey: xAxisKey,
        numericKeys: numericKeys,
        colors: DEFAULT_CHART_COLORS.slice(0, numericKeys.length)
    };
}
/**
 * Generates chart configuration from analyzed data
 */
export function generateChartConfig(analysis) {
    var config = {};
    analysis.numericKeys.forEach(function (key, index) {
        config[key] = {
            label: formatLabel(key),
            color: analysis.colors[index % analysis.colors.length]
        };
    });
    return config;
}
/**
 * Formats a key name into a human-readable label
 */
export function formatLabel(key) {
    return key
        .replace(/([A-Z])/g, ' $1') // Add space before capitals
        .replace(/[_-]/g, ' ') // Replace underscores and hyphens with spaces
        .replace(/\b\w/g, function (l) { return l.toUpperCase(); }) // Capitalize first letter of each word
        .trim();
}
/**
 * Transforms user JSON data for specific chart variants
 */
export function transformDataForVariant(inputData) {
    var _a;
    var data = inputData.data, _b = inputData.variant, variant = _b === void 0 ? LineChartVariant.MULTIPLE : _b;
    var analysis = analyzeChartData(data);
    var transformedData = __spreadArray([], data, true);
    var config = generateChartConfig(analysis);
    // Special transformations for specific variants
    switch (variant) {
        case LineChartVariant.DOTS_COLORS:
            // Add fill property for colored dots variant
            transformedData = data.map(function (item, index) { return (__assign(__assign({}, item), { fill: DEFAULT_CHART_COLORS[index % DEFAULT_CHART_COLORS.length] })); });
            break;
        case LineChartVariant.DEFAULT:
        case LineChartVariant.LINEAR:
        case LineChartVariant.STEP:
        case LineChartVariant.DOTS:
        case LineChartVariant.DOTS_CUSTOM:
        case LineChartVariant.LABEL:
        case LineChartVariant.LABEL_CUSTOM:
            // For single-line variants, prefer the first numeric key
            if (analysis.numericKeys.length > 1) {
                var primaryKey = analysis.numericKeys[0];
                config = (_a = {},
                    _a[primaryKey] = config[primaryKey],
                    _a);
            }
            break;
        case LineChartVariant.MULTIPLE:
        default:
            // Use all numeric keys for multiple line chart
            break;
    }
    return {
        data: transformedData,
        config: config,
        analysis: analysis
    };
}
/**
 * Validates data compatibility with chart variants
 */
export function validateDataForVariant(data, variant) {
    var analysis = analyzeChartData(data);
    switch (variant) {
        case LineChartVariant.MULTIPLE:
            if (analysis.numericKeys.length < 2) {
                return {
                    valid: false,
                    message: "Multiple line chart requires at least 2 numeric data series"
                };
            }
            break;
        case LineChartVariant.DOTS_COLORS:
            // This variant works better with categorical data
            if (data.length > 20) {
                return {
                    valid: false,
                    message: "Colored dots variant works best with fewer than 20 data points"
                };
            }
            break;
        case LineChartVariant.LABEL:
        case LineChartVariant.LABEL_CUSTOM:
            if (data.length > 15) {
                return {
                    valid: false,
                    message: "Label variants can become cluttered with more than 15 data points"
                };
            }
            break;
    }
    return { valid: true };
}
/**
 * Auto-suggests the best chart variant based on data characteristics
 */
export function suggestChartVariant(data) {
    var analysis = analyzeChartData(data);
    var dataSize = data.length;
    // Multiple series data
    if (analysis.numericKeys.length >= 2) {
        return {
            suggested: LineChartVariant.MULTIPLE,
            reason: "Data contains multiple numeric series, perfect for comparison",
            alternatives: [
                { variant: LineChartVariant.DOTS, reason: "Add dots to highlight individual data points" },
                { variant: LineChartVariant.DEFAULT, reason: "Simple view focusing on primary metric" }
            ]
        };
    }
    // Single series - choose based on data size and characteristics
    if (dataSize <= 10) {
        return {
            suggested: LineChartVariant.LABEL,
            reason: "Small dataset benefits from showing values as labels",
            alternatives: [
                { variant: LineChartVariant.DOTS, reason: "Emphasize individual data points" },
                { variant: LineChartVariant.DOTS_CUSTOM, reason: "Custom styling for visual impact" }
            ]
        };
    }
    if (dataSize <= 20) {
        return {
            suggested: LineChartVariant.DOTS,
            reason: "Medium dataset with visible data points for clarity",
            alternatives: [
                { variant: LineChartVariant.DEFAULT, reason: "Clean line without dots" },
                { variant: LineChartVariant.STEP, reason: "Step chart if data represents discrete states" }
            ]
        };
    }
    // Large datasets
    return {
        suggested: LineChartVariant.DEFAULT,
        reason: "Large dataset benefits from clean line visualization",
        alternatives: [
            { variant: LineChartVariant.LINEAR, reason: "Straight lines for trend analysis" },
            { variant: LineChartVariant.STEP, reason: "Step chart for discrete data changes" }
        ]
    };
}
/**
 * Calculates trend information from data
 */
export function calculateTrendInfo(data, primaryKey) {
    if (data.length < 2)
        return undefined;
    var firstValue = Number(data[0][primaryKey]) || 0;
    var lastValue = Number(data[data.length - 1][primaryKey]) || 0;
    if (firstValue === 0)
        return undefined;
    var percentageChange = ((lastValue - firstValue) / firstValue) * 100;
    return {
        trend: percentageChange >= 0 ? "up" : "down",
        percentage: Math.abs(Math.round(percentageChange * 10) / 10),
        period: data.length > 7 ? "period" : "timeframe"
    };
}
